import { create } from 'zustand';
import {
    persist,
    createJSONStorage,
    subscribeWithSelector
} from 'zustand/middleware';
import { share } from 'shared-zustand';
import { v4 } from 'uuid';

interface IAppState {
    accessToken?: string;
    refreshToken?: string;
    isAuthorized: boolean;
    expireAt?: number;
    customerId?: string;
    lastTimeUpdateAt?: number;
    setCustomerId: (customerId: string) => void;
    setTokens: (
        accessToken: string,
        refreshToken: string,
        expireIn: number
    ) => void;
    logout: () => void;
    removeAccessToken: () => void;
    externalId: string;
}

const initialState = {
    accessToken: undefined,
    refreshToken: undefined,
    expireAt: 0,
    lastTimeUpdateAt: 0,
    isAuthorized: false,
    customerId: undefined,
    externalId: v4()
};

const SECOND_IN_MS = 1000;

const useAppStore = create<IAppState>()(
    persist(
        subscribeWithSelector((set) => ({
            ...initialState,
            setCustomerId: (customerId) => {
                set({ customerId });
            },
            setTokens: (accessToken, refreshToken, expireIn) => {
                const now = Date.now();
                set({
                    accessToken,
                    refreshToken,
                    isAuthorized: true,
                    expireAt: now + expireIn * SECOND_IN_MS,
                    lastTimeUpdateAt: now
                });
            },
            logout: () => {
                set(initialState);
            },
            removeAccessToken: () => {
                set({
                    accessToken: undefined,
                    isAuthorized: false,
                    expireAt: 0,
                    lastTimeUpdateAt: 0
                });
            }
        })),
        {
            name: 'app-store',
            storage: createJSONStorage(() => globalThis.localStorage),
            partialize: ({ externalId, ...state }) => state,
            version: 1
        }
    )
);

if ('BroadcastChannel' in globalThis) {
    share('expireAt', useAppStore);
    share('accessToken', useAppStore);
    share('refreshToken', useAppStore);
    share('isAuthorized', useAppStore);
    share('lastTimeUpdateAt', useAppStore);
}

export default useAppStore;
